import {
  camelize,
  type Component,
  type ComponentPublicInstance,
  computed,
  defineCustomElement,
  h,
  isRef,
  type MaybeRef,
  nextTick,
  onBeforeUpdate,
  type Ref,
  ref,
  type SetupContext,
  shallowReactive,
  unref,
  useHost,
  watch,
} from "vue";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DATA_ATTR_REGEX } from "../constants/dataAttributes";
import { customElementTag, SCRIPT_PROP_SELECTOR, SCRIPT_PROPS_KEY } from "../constants/customElementNames";
import {
  PROP_TYPE_HINT,
  type PropTypeHint,
  type SlotDefinition,
  type VueCustomElementSource,
} from "../types/customElements";
import {
  buildSlotOutlets,
  type CapturedSlotNodes,
  captureSlotNodes,
  detachUnprojectedSlotNodes,
  HOST_MOUNTING_KEY,
  HOST_SLOT_NODES_KEY,
  installSlotNodeHostOverrides,
  readCapturedSlotNodes,
} from "./slotNodeProjection";

const HOST_SLOT_CONTENT_CAPTURED_KEY = "__easy8SlotContentCaptured";
const HOST_CONNECTION_PENDING_KEY = "__easy8ConnectionPending";
const HOST_READY_KEY = "__easy8Ready";
const HOST_READY_PROMISE_KEY = "__easy8ReadyPromise";
const HOST_READY_RESOLVE_KEY = "__easy8ReadyResolve";
const HOST_PARENT_WAIT_GENERATION_KEY = "__easy8ParentWaitGeneration";
const MODEL_VALUE_UPDATE_EVENT = "update:modelValue";
/** Vue's own `emit` uses the same prefix length to resolve model modifiers. */
const MODEL_UPDATE_PREFIX = "update:";

type RegisterCustomElementOptions = {
  forceWrapper?: boolean;
  /**
   * Events to forward to the host. A plain array is a fixed contract. A `Ref` marks a registry registration whose
   * emits are discovered after lazy loading, and additionally enables automatic named-model synchronization; pass one
   * only together with `forceWrapper`, because an empty ref alone does not request a wrapper.
   */
  forwardedEmits?: MaybeRef<string[]>;
  /**
   * Required for an async `component` with a `loadingComponent`: Vue forwards the inner ref to that placeholder, and
   * without this option the light DOM would be swept while the placeholder shows.
   */
  pendingComponent?: Component;
};

type CoercingWrapperOptions = {
  propTypes: Record<string, PropTypeHint>;
  modelValueType?: PropTypeHint;
  namedSlots?: SlotDefinition[];
  exposedMethods?: string[];
  /** See `RegisterCustomElementOptions.forwardedEmits`: a `Ref` selects registry discovery and named-model sync. */
  forwardedEmits?: MaybeRef<string[]>;
  pendingComponent?: Component;
};

type InnerComponentRef = Ref<ComponentPublicInstance | null>;
/** A forwarded ref may also hold an element (functional root component). */
type MountedInner = { $?: { type?: unknown } };

// Needed to parse time-only values ("HH:mm") with an explicit format; safe to extend repeatedly.
dayjs.extend(customParseFormat);

const TIME_ONLY_FORMATS = ["HH:mm:ss", "HH:mm"];

const coerceDateProp = (value: string, hint: PropTypeHint): unknown => {
  // Time-only strings ("14:30") are not parseable by plain dayjs(); try explicit time formats first,
  // then fall back to plain parsing so full datetime strings keep working.
  const timeOnly = hint === PROP_TYPE_HINT.Time ? dayjs(value, TIME_ONLY_FORMATS, true) : null;
  const date = timeOnly?.isValid() ? timeOnly : dayjs(value);
  if (!date.isValid()) {
    console.warn(`[easy8 custom elements] Invalid date value: "${value}"`);
    return null;
  }
  return date;
};

const coerceProp = (value: string, hint: PropTypeHint): unknown => {
  if (hint === PROP_TYPE_HINT.Boolean) return value === "true";
  if (hint === PROP_TYPE_HINT.Number) return value.trim() === "" ? undefined : Number(value);
  if (hint === PROP_TYPE_HINT.Date || hint === PROP_TYPE_HINT.Time) return coerceDateProp(value, hint);
  return value;
};

const parseSingleScriptProp = (script: HTMLScriptElement): [string, unknown] | undefined => {
  const propName = script.dataset.prop;
  if (!propName) return undefined;

  const raw = script.textContent?.trim();
  if (!raw) return undefined;

  try {
    return [propName, JSON.parse(raw)];
  } catch {
    console.warn(`[easy8 custom elements] Failed to parse <script data-prop="${propName}"> JSON`);
    return undefined;
  }
};

const parseScriptProps = (host: HTMLElement): [Record<string, unknown>, HTMLScriptElement[]] => {
  const result: Record<string, unknown> = {};
  const scripts = Array.from(host.querySelectorAll<HTMLScriptElement>(SCRIPT_PROP_SELECTOR)).filter(
    script => script.parentElement === host
  );

  for (const script of scripts) {
    const parsed = parseSingleScriptProp(script);
    if (parsed) {
      result[parsed[0]] = parsed[1];
    }
  }

  return [result, scripts];
};

const readScriptPropsFromHost = (): Record<string, unknown> => {
  const host = useHost();
  return ((host as unknown as Record<string, unknown>)?.[SCRIPT_PROPS_KEY] as Record<string, unknown>) ?? {};
};

const resolveInitialModelValue = (
  scriptProps: Record<string, unknown>,
  attrValue: string | undefined,
  hint?: PropTypeHint
): unknown => {
  if (scriptProps.value !== undefined) return scriptProps.value;
  if (attrValue !== undefined && hint) return coerceProp(attrValue, hint);
  return undefined;
};

const extractDataProps = (
  attrs: Record<string, unknown>,
  propTypes: Record<string, PropTypeHint>
): Record<string, unknown> => {
  const RESERVED_PROPS = ["prop", "cy"];
  const result: Record<string, unknown> = {};

  for (const [key, rawValue] of Object.entries(attrs)) {
    if (!DATA_ATTR_REGEX.test(key)) continue;

    const propName = key.slice(4, 5).toLowerCase() + key.slice(5); // dataDisabled → disabled
    if (RESERVED_PROPS.includes(propName)) continue;

    const hint = propTypes[propName];
    result[propName] = hint && typeof rawValue === "string" ? coerceProp(rawValue, hint) : rawValue;
  }

  return result;
};

const initModelValue = ({
  dataProps,
  scriptProps,
  modelValueType,
  force = false,
  fallbackValue,
}: {
  dataProps: Record<string, unknown>;
  scriptProps: Record<string, unknown>;
  modelValueType?: PropTypeHint;
  force?: boolean;
  fallbackValue?: unknown;
}): Ref<unknown> | null => {
  const hasScriptValue = Object.prototype.hasOwnProperty.call(scriptProps, "value");
  if (!force && !modelValueType && !hasScriptValue) return null;

  const initial =
    hasScriptValue || (modelValueType && dataProps.value !== undefined)
      ? resolveInitialModelValue(scriptProps, dataProps.value as string | undefined, modelValueType)
      : fallbackValue;
  delete dataProps.value;
  delete scriptProps.value;
  return ref(initial);
};

const buildModelValueBindings = (
  internalModel: Ref<unknown> | null,
  forwardUpdate?: (value: unknown) => void
): Record<string, unknown> => {
  if (!internalModel) return {};

  return {
    modelValue: internalModel.value,
    "onUpdate:modelValue": (val: unknown) => {
      internalModel.value = val;
      forwardUpdate?.(val);
    },
  };
};

const filterNonDataAttrs = (attrs: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(attrs)) {
    if (!DATA_ATTR_REGEX.test(key)) {
      result[key] = value;
    }
  }

  return result;
};

const buildForwardedEventListeners = (
  forwardedEmits: string[] | undefined,
  emit: SetupContext["emit"]
): Record<string, (...args: unknown[]) => void> =>
  Object.fromEntries(
    (forwardedEmits ?? [])
      .filter(event => event !== MODEL_VALUE_UPDATE_EVENT)
      .map(event => [
        `on${event.charAt(0).toUpperCase()}${event.slice(1)}`,
        (...args: unknown[]) => emit(event, ...args),
      ])
  );

const dispatchModelValueUpdate =
  (host: HTMLElement) =>
  (value: unknown): void => {
    host.dispatchEvent(
      new CustomEvent(MODEL_VALUE_UPDATE_EVENT, {
        bubbles: true,
        composed: true,
        detail: [value],
      })
    );
  };

const exposeHostMethods = (host: HTMLElement, innerRef: InnerComponentRef, methods: string[] = []): void => {
  for (const method of methods) {
    (host as unknown as Record<string, unknown>)[method] = (...args: unknown[]) =>
      (innerRef.value as unknown as Record<string, (...a: unknown[]) => unknown> | null)?.[method]?.(...args);
  }
};

/**
 * Calls `onMounted` after the render flush in which the wrapped component mounted. Vue's async wrapper forwards the
 * ref to its loading placeholder and to whatever the loader resolves to (the registry resolves failures to its
 * load-error component), so only the pending placeholder is skipped.
 */
const watchInnerComponentMounted = (
  innerRef: InnerComponentRef,
  pendingComponent: Component | undefined,
  onMounted: () => void
): void => {
  watch(innerRef, instance => {
    if (instance && (instance as MountedInner).$?.type !== pendingComponent) void nextTick(onMounted);
  });
};

const extractRenderedDataProps = (
  attrs: Record<string, unknown>,
  propTypes: Record<string, PropTypeHint>,
  omitValue: boolean
): Record<string, unknown> => {
  const dataProps = extractDataProps(attrs, propTypes);
  if (omitValue) delete dataProps.value;
  return dataProps;
};

const synchronizeNamedModelsFromDataProps = (
  namedModels: Record<string, unknown>,
  currentDataProps: Record<string, unknown>,
  previousDataProps: Record<string, unknown>,
  scriptProps: Record<string, unknown>
): void => {
  const keys = new Set([...Object.keys(currentDataProps), ...Object.keys(previousDataProps)]);

  for (const key of keys) {
    if (!Object.hasOwn(namedModels, key) || Object.hasOwn(scriptProps, key)) continue;
    if (Object.is(currentDataProps[key], previousDataProps[key])) continue;

    if (Object.hasOwn(currentDataProps, key)) namedModels[key] = currentDataProps[key];
    else delete namedModels[key];
  }
};

const createCoercingWrapper = (
  component: Component | VueCustomElementSource,
  options: CoercingWrapperOptions
): Component => ({
  // Read once, at registration: a component option cannot be reactive. Nothing observable depends on it, because
  // Vue replaces `instance.emit` wholesale for custom-element roots, so dispatch never consults this declaration.
  // The reactive `forwardedEventListeners` below is what actually delivers the discovered emits.
  emits: unref(options.forwardedEmits),
  inheritAttrs: false,
  setup(_props: Record<string, unknown>, { slots, attrs, emit }: SetupContext) {
    const host = useHost() as HTMLElement;
    const scriptProps = readScriptPropsFromHost();
    const initialDataProps = extractDataProps(attrs, options.propTypes);
    // Only registry registrations supply reactive discovery metadata; direct registrations keep their model contract.
    const automaticModels = isRef(options.forwardedEmits);
    const readModelValueFallback = () =>
      automaticModels
        ? { ...attrs, ...extractDataProps(attrs, options.propTypes), ...scriptProps }.modelValue
        : undefined;
    const forwardsModelValueUpdate = computed(
      () => unref(options.forwardedEmits)?.includes(MODEL_VALUE_UPDATE_EVENT) ?? false
    );
    let internalModel = initModelValue({
      dataProps: initialDataProps,
      scriptProps,
      modelValueType: options.modelValueType,
      force: forwardsModelValueUpdate.value,
      fallbackValue: readModelValueFallback(),
    });
    // Late model discovery must initialize host state before the lazy child's first render or emit.
    watch(
      forwardsModelValueUpdate,
      forwards => {
        if (forwards && !internalModel) {
          const currentDataProps = extractDataProps(attrs, options.propTypes);
          internalModel = initModelValue({
            dataProps: currentDataProps,
            scriptProps,
            modelValueType: options.modelValueType,
            force: true,
            fallbackValue: readModelValueFallback(),
          });
        }
      },
      { flush: "sync" }
    );
    const slotOutlets = buildSlotOutlets(host, readCapturedSlotNodes(host), options.namedSlots);
    const innerRef: InnerComponentRef = ref(null);
    // Shallow on purpose: a deep `ref` would hand the child a reactive proxy of the emitted payload instead of the
    // value it emitted, breaking identity comparisons for object and array models.
    const namedModels = shallowReactive<Record<string, unknown>>({});
    // A named `update:*` is forwarded as a DOM event AND mirrored back as a prop, because forwarding installs an
    // `onUpdate:<name>` listener, which takes Vue's `defineModel` out of its uncontrolled local mode. Names that are
    // not models of the child are mirrored too: the adapter cannot see the child's prop list, and gating on the
    // supplied props would break models that start with no value at all (for example `ds-dialog`'s `open`).
    const syncNamedModel = (event: string, value: unknown): void => {
      const name = camelize(event.slice(MODEL_UPDATE_PREFIX.length));
      // Skipping no-op writes keeps a child that re-emits while reacting to its own prop from looping.
      if (name && namedModels[name] !== value) namedModels[name] = value;
    };
    let previousDataProps = initialDataProps;
    onBeforeUpdate(() => {
      const currentDataProps = extractDataProps(attrs, options.propTypes);
      synchronizeNamedModelsFromDataProps(namedModels, currentDataProps, previousDataProps, scriptProps);
      previousDataProps = currentDataProps;
    });
    const forwardedEventListeners = computed(() =>
      buildForwardedEventListeners(unref(options.forwardedEmits), (event, ...args: unknown[]) => {
        if (automaticModels && event.startsWith(MODEL_UPDATE_PREFIX)) {
          syncNamedModel(event, args[0]);
        }
        const original = args[0];
        if (
          original instanceof Event &&
          original.type === event &&
          original.bubbles &&
          !original.cancelBubble &&
          original.composedPath().includes(host)
        )
          return;

        emit(event, ...args);
      })
    );

    exposeHostMethods(host, innerRef, options.exposedMethods);
    // Light DOM the component did not claim through a slot outlet is swept once the real component mounted.
    watchInnerComponentMounted(innerRef, options.pendingComponent, () => detachUnprojectedSlotNodes(host));

    return () => {
      const dataProps = extractRenderedDataProps(attrs, options.propTypes, Boolean(internalModel));

      return h(
        component as Parameters<typeof h>[0],
        {
          ref: innerRef,
          ...filterNonDataAttrs(attrs),
          ...dataProps,
          ...scriptProps,
          ...namedModels,
          ...buildModelValueBindings(
            internalModel,
            forwardsModelValueUpdate.value ? dispatchModelValueUpdate(host) : undefined
          ),
          ...forwardedEventListeners.value,
        },
        { ...slots, ...slotOutlets }
      );
    };
  },
});

type WrapperArgs = {
  propTypes?: Record<string, PropTypeHint>;
  modelValueType?: PropTypeHint;
  slots?: SlotDefinition[];
  exposedMethods?: string[];
  options?: RegisterCustomElementOptions;
};

const wrapperFeatureFlags = ({ propTypes, modelValueType, slots, exposedMethods, options }: WrapperArgs): unknown[] => [
  propTypes,
  modelValueType,
  slots?.length,
  exposedMethods?.length,
  unref(options?.forwardedEmits)?.length,
];

/** Wrapper options when a wrapper feature is requested or forced; `undefined` when the component is defined as is. */
const resolveWrapperOptions = (args: WrapperArgs): CoercingWrapperOptions | undefined => {
  const { forceWrapper, forwardedEmits, pendingComponent } = args.options ?? {};
  if (forceWrapper !== true && !wrapperFeatureFlags(args).some(Boolean)) return undefined;

  return {
    propTypes: args.propTypes ?? {},
    modelValueType: args.modelValueType,
    namedSlots: args.slots,
    exposedMethods: args.exposedMethods,
    forwardedEmits,
    pendingComponent,
  };
};

export const registerCustomElementFromVue = (
  component: Component | VueCustomElementSource,
  name: string,
  propTypes?: Record<string, PropTypeHint>,
  modelValueType?: PropTypeHint,
  slots?: SlotDefinition[],
  exposedMethods?: string[],
  options?: RegisterCustomElementOptions
) => {
  const wrapperOptions = resolveWrapperOptions({ propTypes, modelValueType, slots, exposedMethods, options });
  const target = (
    wrapperOptions ? createCoercingWrapper(component, wrapperOptions) : component
  ) as VueCustomElementSource;

  const customElementBase = defineCustomElement(target, {
    shadowRoot: false,
  });

  class Easy8CustomElement extends customElementBase {
    [SCRIPT_PROPS_KEY]: Record<string, unknown> = {};
    [HOST_SLOT_NODES_KEY]: CapturedSlotNodes | undefined;
    [HOST_SLOT_CONTENT_CAPTURED_KEY] = false;
    [HOST_MOUNTING_KEY] = false;
    [HOST_CONNECTION_PENDING_KEY] = false;
    [HOST_READY_KEY] = false;
    [HOST_READY_RESOLVE_KEY]: () => void = () => {};
    [HOST_READY_PROMISE_KEY] = new Promise<void>(resolve => {
      this[HOST_READY_RESOLVE_KEY] = resolve;
    });
    [HOST_PARENT_WAIT_GENERATION_KEY] = 0;

    connectedCallback() {
      if (this[HOST_CONNECTION_PENDING_KEY]) return;

      this.beginConnectionCycle();
      this.connectAfterScriptCleanup(this.captureInitialHostContent());
    }

    private beginConnectionCycle(): void {
      this[HOST_READY_RESOLVE_KEY]();
      this[HOST_READY_KEY] = false;
      this[HOST_READY_PROMISE_KEY] = new Promise<void>(resolve => {
        this[HOST_READY_RESOLVE_KEY] = resolve;
      });
    }

    private captureInitialHostContent(): HTMLScriptElement[] {
      if (this[HOST_SLOT_CONTENT_CAPTURED_KEY]) return [];

      // Parse BEFORE super.connectedCallback() which triggers Vue mount.
      // At this point child <script> tags are already in the DOM.
      const [parsed, scripts] = parseScriptProps(this);
      if (Object.keys(parsed).length > 0) {
        this[SCRIPT_PROPS_KEY] = parsed;
      }
      // References only: the light DOM stays in place until the wrapped component projects it into its outlets.
      this[HOST_SLOT_NODES_KEY] = captureSlotNodes(this, scripts);
      this[HOST_SLOT_CONTENT_CAPTURED_KEY] = true;

      return scripts;
    }

    private connectAfterScriptCleanup(scripts: HTMLScriptElement[]): void {
      if (scripts.length === 0) {
        this.connectWhenParentReady();
        return;
      }

      // Let synchronously registered ancestors capture the server markup, then remove transport
      // scripts before Vue parses this host's light-DOM slots.
      this[HOST_CONNECTION_PENDING_KEY] = true;
      queueMicrotask(() => {
        scripts.forEach(script => {
          if (script.parentElement === this) script.remove();
        });
        this[HOST_CONNECTION_PENDING_KEY] = false;
        if (this.isConnected) this.connectWhenParentReady();
      });
    }

    private connectWhenParentReady(): void {
      const parentReady = this.parentReadyPromise();
      if (!parentReady) {
        this[HOST_PARENT_WAIT_GENERATION_KEY] += 1;
        this.mountPreservingSlotNodes();
        this[HOST_READY_KEY] = true;
        this[HOST_READY_RESOLVE_KEY]();
        return;
      }

      const waitGeneration = (this[HOST_PARENT_WAIT_GENERATION_KEY] += 1);
      void parentReady.then(() => {
        if (waitGeneration === this[HOST_PARENT_WAIT_GENERATION_KEY] && this.isConnected) {
          this.connectWhenParentReady();
        }
      });
    }

    /** Vue mounts synchronously inside `connectedCallback`; the flag lets the host keep its slot nodes (wrapper mode). */
    private mountPreservingSlotNodes(): void {
      this[HOST_MOUNTING_KEY] = true;
      try {
        super.connectedCallback();
      } finally {
        this[HOST_MOUNTING_KEY] = false;
      }
    }

    private parentReadyPromise(): Promise<void> | undefined {
      let parent = this.parentElement;

      while (parent) {
        if (HOST_READY_KEY in parent) {
          const customElementParent = parent as HTMLElement & {
            [HOST_READY_KEY]: boolean;
            [HOST_READY_PROMISE_KEY]: Promise<void>;
          };
          return customElementParent[HOST_READY_KEY] ? undefined : customElementParent[HOST_READY_PROMISE_KEY];
        }
        parent = parent.parentElement;
      }

      return undefined;
    }
  }

  if (wrapperOptions) {
    installSlotNodeHostOverrides(Easy8CustomElement.prototype);
  }

  const customElementFullTag = customElementTag(name);

  if (!customElements.get(customElementFullTag)) {
    customElements.define(customElementFullTag, Easy8CustomElement);
  }
};
